import { User } from '../types/models';
import { supabase } from './supabase';
import { trackEvent, EventCategory, EventAction, trackLogin } from './analytics';

// 사용자 정보 자동 갱신 주기.
// 이름/직위/등급은 자주 바뀌지 않으므로 짧게 잡을 이유가 없고,
// 짧게 잡으면 접속자 수만큼 Supabase 트래픽이 그대로 늘어난다.
export const USER_AUTO_REFRESH_INTERVAL_MS = 30 * 60 * 1000;

// 사용자 프로필/목록 캐시 수명 (갱신 주기와 별개로 관리한다)
export const USER_CACHE_TTL_MS = 30 * 60 * 1000;
const USER_PROFILE_CACHE_KEY = 'user_profile';
const ALL_USERS_CACHE_KEY = 'all_users';

const readTimedCache = <T>(key: string, maxAgeMs: number): T | null => {
  const cachedValue = sessionStorage.getItem(key);
  if (!cachedValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(cachedValue);
    const cacheTime = parsedValue._cacheTime || 0;

    if (Date.now() - cacheTime < maxAgeMs) {
      return parsedValue as T;
    }

    sessionStorage.removeItem(key);
  } catch (e) {
    console.warn(`캐시 파싱 오류 (${key}):`, e);
    sessionStorage.removeItem(key);
  }

  return null;
};

const writeTimedCache = (key: string, value: unknown) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({
      ...((value as Record<string, unknown>) || {}),
      _cacheTime: Date.now(),
    }));
  } catch (e) {
    console.warn(`캐시 저장 오류 (${key}):`, e);
  }
};

export const clearUserCaches = () => {
  sessionStorage.removeItem(USER_PROFILE_CACHE_KEY);
  sessionStorage.removeItem(ALL_USERS_CACHE_KEY);
  sessionStorage.removeItem('cached_users');
};

/**
 * 인증된 사용자 정보와 public.users 테이블의 정보를 매핑하여 완전한 사용자 정보를 반환
 */
export const getUserProfile = async (options?: { forceRefresh?: boolean }): Promise<User | null> => {
  try {
    if (!options?.forceRefresh) {
      const cachedUserProfile = readTimedCache<(User & { _cacheTime?: number })>(
        USER_PROFILE_CACHE_KEY,
        USER_CACHE_TTL_MS
      );
      if (cachedUserProfile) {
        console.log('캐시된 사용자 프로필 사용');
        delete cachedUserProfile._cacheTime;
        return cachedUserProfile as User;
      }
    }
    
    // 1. Supabase 인증 시스템에서 사용자 정보 가져오기
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      console.error('인증된 세션이 없습니다.');
      return null;
    }
    
    const { data, error } = await supabase.auth.getUser();
    
    if (error || !data.user) {
      console.error('인증된 사용자 정보가 없습니다:', error);
      return null;
    }
    
    const authUser = data.user;
    console.log('인증 사용자 정보 가져오기 성공:', authUser);
    
    // 2. public.users 테이블에서 추가 정보 가져오기 (활성 사용자만)
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('*')
      .eq('id', authUser.id)
      .eq('is_active', true)
      .single();
    
    let userProfile: User;
    
    if (userError) {
      console.error('DB 사용자 정보 가져오기 에러:', userError);
      
      // 사용자 테이블에 정보가 없을 경우 기본 정보로 매핑
      userProfile = {
        id: authUser.id,
        email: authUser.email || '',
        name: authUser.user_metadata?.name || authUser.email?.split('@')[0] || '(정보 없음)',
        team: '',
        position: '',
        role: '',
        password_changed_from_initial: false
      };
    } else {
      // 3. 사용자 정보 매핑
      userProfile = userData as User;
      console.log('사용자 정보 가져오기 성공:', userData);
    }
    
    // 캐시에 사용자 정보 저장 (만료 시간 포함)
    writeTimedCache(USER_PROFILE_CACHE_KEY, userProfile);
    
    return userProfile;
  } catch (error) {
    console.error('사용자 정보 가져오기 예외 발생:', error);
    return null;
  }
};

/**
 * 로그인 후 사용자 정보 매핑
 */
export const signIn = async (email: string, password: string) => {
  try {
    // 1. Supabase 인증 시스템으로 로그인
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (authError || !authData.user) {
      throw authError || new Error('로그인 실패: 사용자 정보를 가져올 수 없습니다.');
    }
    
    // 2. public.users 테이블에서 추가 정보 가져오기 (활성 사용자만)
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('*')
      .eq('id', authData.user.id)
      .eq('is_active', true)
      .single();
    
    if (userError) {
      console.warn('DB 사용자 정보 가져오기 실패, 기본 정보만 반환합니다:', userError);
      // 사용자 테이블에 정보가 없을 경우 기본 정보로 매핑
      return {
        user: {
          id: authData.user.id,
          email: authData.user.email || '',
          name: authData.user.user_metadata?.name || authData.user.email?.split('@')[0] || '(정보 없음)',
          team: '',
          position: '',
          role: '',
          password_changed_from_initial: false
        },
        session: authData.session,
        last_sign_in_at: authData.user.last_sign_in_at || null
      };
    }
    // 3. 사용자 정보 매핑 및 반환
    const user = userData as User;
    
    // 로그인 성공 이벤트 추적
    trackLogin(user, 'email');
    return {
      user,
      session: authData.session,
      last_sign_in_at: authData.user.last_sign_in_at || null
    };
  } catch (error: any) {
    console.error('로그인 처리 중 오류 발생:', error);
    throw error;
  }
};

// 로그아웃 기능
export const signOut = async () => {
    // scope: 'global'로 설정하여 모든 기기에서 로그아웃 처리
    const { error } = await supabase.auth.signOut({ scope: 'global' });
    
    if (error) {
        console.error('로그아웃 에러:', error);
        throw error;
    }
    
    // 로컬 스토리지의 세션 관련 데이터도 모두 제거
    localStorage.removeItem('supabase.auth.token');
    localStorage.removeItem('currentUser');
    
    // 세션 스토리지의 사용자 정보도 제거
    clearUserCaches();

    // 세션 쿠키도 제거
    document.cookie.split(';').forEach(cookie => {
        const [name] = cookie.trim().split('=');
        if (name.includes('supabase') || name.includes('auth')) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
    });
};

/**
 * 이메일이 users 테이블에 존재하는지 확인
 */
export const checkEmailExists = async (email: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('email')
      .eq('email', email)
      .eq('is_active', true)
      .maybeSingle();
    
    if (error) {
      console.error('이메일 존재 여부 확인 중 오류:', error);
      throw error;
    }
    
    return data !== null;
  } catch (error) {
    console.error('이메일 확인 중 예외 발생:', error);
    throw error;
  }
};

/**
 * 비밀번호 재설정 이메일 발송
 */
export const resetPassword = async (email: string) => {
  try {
    const BASE_URL = process.env.REACT_APP_BASE_URL || window.location.origin;
    const RESET_PASSWORD_PATH = process.env.REACT_APP_RESET_PASSWORD_PATH || '/reset-password';
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${BASE_URL}${RESET_PASSWORD_PATH}`,
    });
    
    if (error) {
      console.error('비밀번호 재설정 메일 발송 실패:', error);
      throw error;
    }
    
    console.log('비밀번호 재설정 이메일 발송 성공:', { email });
    
    // 비밀번호 재설정 이벤트 추적
    // LoginPage.tsx에서도 추적하지만 여기서도 추가
    trackEvent(EventCategory.AUTH, EventAction.PASSWORD_RESET, email);
    
    return { success: true, data };
  } catch (error) {
    console.error('비밀번호 재설정 이메일 발송 오류:', error);
    throw error;
  }
};

/**
 * 모든 사용자 가져오기 
 */
export const getAllUsers = async (options?: { forceRefresh?: boolean }): Promise<User[]> => {
  try {
    const cachedAllUsers = !options?.forceRefresh
      ? readTimedCache<{
          _cacheTime?: number;
          users: User[];
        }>(ALL_USERS_CACHE_KEY, USER_CACHE_TTL_MS)
      : null;

    if (cachedAllUsers) {
      console.log('캐시된 모든 사용자 정보 사용');
      return cachedAllUsers.users as User[];
    }
    const staleAllUsers = sessionStorage.getItem(ALL_USERS_CACHE_KEY);
    if (staleAllUsers && options?.forceRefresh) {
      try {
        const cachedData = JSON.parse(staleAllUsers);
        if (!Array.isArray(cachedData.users)) {
          sessionStorage.removeItem(ALL_USERS_CACHE_KEY);
        }
      } catch (e) {
        console.warn('기존 사용자 캐시 파싱 오류:', e);
        sessionStorage.removeItem(ALL_USERS_CACHE_KEY);
      }
    }

    // 세션 확인
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      console.error('인증된 세션이 없습니다.');
      return [];
    }

    // 인증 헤더를 명시적으로 설정하여 활성 사용자 정보만 조회
    const { data, error } = await supabase
      .from('users')
      .select('id, email, name, team, position, role, password_changed_from_initial')
      .eq('is_active', true);

    if (error) {
      console.error('Error fetching all users:', error);
      
      // 캐시된 데이터가 있는지 확인하고 오류 발생 시 캐시 사용
      if (staleAllUsers) {
        try {
          const fallbackData = JSON.parse(staleAllUsers);
          console.warn('사용자 목록 조회 오류, 캐시된 데이터 사용');
          return fallbackData.users as User[];
        } catch (e) {
          // 캐시 파싱 오류 무시
        }
      }
      
      throw error;
    }
    
    // 데이터를 User 인터페이스에 맞게 매핑하고 role 필드가 없으면 기본값 'user' 추가
    const users: User[] = (data || []).map(user => ({
      id: user.id,
      email: user.email,
      name: user.name,
      team: user.team || '',
      position: user.position || '',
      role: user.role || 'error',
      password_changed_from_initial: user.password_changed_from_initial
    }));
    
    // 캐시에 사용자 목록 저장
    writeTimedCache(ALL_USERS_CACHE_KEY, { users });
    
    return users;
  } catch (error) {
    console.error('모든 사용자 조회 중 오류 발생:', error);
    throw error;
  }
};
